// Actual domain logic should be in a domainLogic.js file, at the appropriate
// level in the hierarchy.
import { ITEM_CALCULATED_FIELDS } from "appConfig";
import { IN_TRANSACTION } from "appConfig";

import { Loading } from "classes/Loading";

import { afterTouchLine } from "services/sosInventoryService/adjustment/afterTouchLine";
import { getEmptyRecord } from "services/sosInventoryService/adjustment/schema";
import {
  updateLineRelatedRecordsItem,
  updateLineItemOnhand,
  removeLotAndSerialValues,
} from "services/sosInventoryService/domainLogic";
import { getRecord, getRecordFrom } from "services/sosInventoryService/sosApi";
import { setOutboundBinsForLines } from "services/utility/bins";
import { reconcileCustomFields } from "services/utility/customFields";
import { getRelatedReferenceObjects } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";

import globalState from "globalState/globalState";
import { linesLoadingOn, linesLoadingOff } from "globalState/loadingSlice";

import { OBJECT_TYPES } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.ADJUSTMENT.fullString;

export async function getTransaction(
  id,
  newFromId,
  newFromObjectType,
  updaters,
  customFieldDefs
) {
  let record, lines, relatedRecords;

  if (id || newFromObjectType) {
    if (id) {
      record = await getRecord(OBJECT_TYPE, id);
    } else {
      // it's a "populate from" request
      record = await getRecordFrom(OBJECT_TYPE, newFromObjectType, newFromId);
      delete record.id;
    }

    record.customFields = reconcileCustomFields(
      customFieldDefs,
      record.customFields
    );

    lines = record.lines;
    delete record.lines;

    // render lines immediately while async fields are computed in the background
    globalState.dispatch(linesLoadingOn());
    updaters.lineHandler({ type: "set", lines });
    updaters.setRecord(record);

    relatedRecords = await getRelatedReferenceObjects(OBJECT_TYPE, record);

    updaters.setRelatedRecords(relatedRecords);

    // get items for each line item, so that we have updated item info for
    // the calculations below
    lines = await updateLineRelatedRecordsItem(
      record.location,
      record.date,
      lines,
      ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
    );

    // set the default bin and the available bins for the dropdown
    lines = await setOutboundBinsForLines(
      record.location,
      lines,
      "bin",
      true, // showAllBins
      true // maintainBins
    );

    // update onhand info in the line items
    lines = updateLineItemOnhand(lines);

    // update newQuantity info in the line items
    lines = lines.map((line) => ({
      ...line,
      newQuantity: line.onhand.plus(line.quantityDiff),
    }));

    // patch only async-computed fields into the already-rendered lines
    updaters.lineHandler({
      type: "updateFields",
      lines,
      fields: [
        "relatedRecords",
        "onhand",
        "availableBins",
        "bin",
        "newQuantity",
      ],
    });
    globalState.dispatch(linesLoadingOff());
  } else {
    // new record
    const { settings } = globalState.getState().userCompanySettings;
    const defaultLocation =
      globalState.getState().userCompanySettings.settings.defaultLocation;
    const numLinesToAdd =
      globalState.getState().userCompanySettings.settings.numLinesToAdd;

    record = {
      ...getEmptyRecord(settings),
      customFields: reconcileCustomFields(customFieldDefs, []),
    };

    lines = Array(numLinesToAdd).fill(EMPTY_LINE_ITEM[OBJECT_TYPE]);

    relatedRecords = {};

    if (defaultLocation) {
      record.location = defaultLocation || null;

      const location = await getRecord(
        "location",
        record.location.id,
        IN_TRANSACTION
      );

      relatedRecords = { ...relatedRecords, location };
    }
    updaters.setRelatedRecords(relatedRecords);
    updaters.setRecord(record);
    updaters.lineHandler({ type: "set", lines });
  }
}

export async function updateTransaction(
  field,
  newValue,
  currentState,
  updaters
) {
  const { record, lines, relatedRecords } = currentState;
  if (!record || !lines || !relatedRecords) {
    handleProgramError(new Error("update Transaction | invalid currentState"));
  }

  let newRecord, newLines, newRelatedRecords;

  switch (field) {
    case "date":
      newRecord = { ...record, date: newValue };
      updaters.setRecord(newRecord);

      if (newValue) {
        globalState.dispatch(linesLoadingOn());
        // show onhand skeleton while async data resolves
        updaters.lineHandler({
          type: "setFields",
          fields: { onhand: new Loading(), newQuantity: new Loading() },
        });

        try {
          newLines = await updateLineRelatedRecordsItem(
            record.location,
            newValue,
            lines,
            ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
          );

          // set the default bin and the available bins for the dropdown
          newLines = await setOutboundBinsForLines(
            record.location,
            newLines,
            "bin",
            true
          );

          // update onhand info in the line items
          newLines = updateLineItemOnhand(newLines);

          newLines = newLines.map((line) => afterTouchLine(line, "uom"));

          // patch only computed fields; preserves any user edits made while async work was in flight
          updaters.lineHandler({
            type: "updateFields",
            lines: newLines,
            fields: [
              "relatedRecords",
              "onhand",
              "availableBins",
              "bin",
              "quantityDiff",
              "newQuantity",
            ],
          });
        } catch (e) {
          handleProgramError(e);
        } finally {
          globalState.dispatch(linesLoadingOff());
        }
      }

      break;

    case "location":
      globalState.dispatch(linesLoadingOn());
      // show onhand skeleton while async data resolves
      updaters.lineHandler({
        type: "setFields",
        fields: { onhand: new Loading(), newQuantity: new Loading() },
      });

      try {
        updaters.setRecord((prevRecord) => ({
          ...prevRecord,
          location: newValue,
        }));
        const location = newValue
          ? await getRecord("location", newValue?.id)
          : null;
        newRelatedRecords = { ...relatedRecords, location };

        newLines = await updateLineRelatedRecordsItem(
          newValue,
          record.date,
          lines,
          ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
        );

        // set the default bin and the available bins for the dropdown
        newLines = await setOutboundBinsForLines(
          newValue,
          newLines,
          "bin",
          true
        );

        // update onhand info in the line items
        newLines = updateLineItemOnhand(newLines);

        newLines = newLines.map((line) => afterTouchLine(line, "uom"));

        newLines = removeLotAndSerialValues(newLines);

        // patch only computed fields; preserves any user edits made while async work was in flight
        updaters.lineHandler({
          type: "updateFields",
          lines: newLines,
          fields: [
            "relatedRecords",
            "onhand",
            "availableBins",
            "bin",
            "quantityDiff",
            "newQuantity",
            "lot",
            "serials",
          ],
        });
      } catch (e) {
        handleProgramError(e);
      } finally {
        globalState.dispatch(linesLoadingOff());
      }

      break;

    case "account":
    case "comment":
    case "customFields":
    case "department":
    case "number":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);
      break;

    default:
      handleProgramError(
        new Error(`updateTransaction | unknown field (${field})`)
      );
  }

  if (newRelatedRecords) {
    updaters.setRelatedRecords(newRelatedRecords);
  }
}
